import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PlusCircle, Edit, Trash2, Check, X as XIcon, RotateCcw, Clock, Building2, MapPin, Globe, Users as UsersIcon, Award, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import RichTextEditor from '@/components/RichTextEditor';
import SustainabilityTagPicker from './SustainabilityTagPicker';

const BACKEND_URL = '';

const SECTORS = ["Supply Chain & Logistics", "Technology & SaaS", "Energy & Utilities", "Manufacturing", "Retail & Consumer", "Finance & Insurance", "Consulting & Advisory", "Transport & Freight", "Waste & Recycling", "Construction & Infrastructure", "Agriculture & Food", "Healthcare & Pharma", "Government & Public Sector", "Education & Research", "Other"];
const SIZES = ["1-10", "11-50", "51-200", "201-500", "501-1000", "1001-5000", "5000+"];

const emptyForm = { name: '', description: '', tagline: '', sector: '', business_type: '', company_size: '', hq_location: '', hq_country: '', website: '', linkedin_url: '', contact_email: '', phone_number: '', is_partner: false, sustainability_focus: [], about_sections: [], latest_news: [] };

export function AdminCompaniesTab() {
  const [companies, setCompanies] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [form, setForm] = useState({ ...emptyForm });
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showSponsorDialog, setShowSponsorDialog] = useState(false);
  const [promotingCompany, setPromotingCompany] = useState(null);
  const [sponsorMode, setSponsorMode] = useState('create'); // 'create' | 'manage'
  const [existingSponsorId, setExistingSponsorId] = useState(null);
  const [sponsorBanners, setSponsorBanners] = useState([]);
  const [bannerForm, setBannerForm] = useState({ banner_type: 'top', placement: 'app', click_url: '', file: null, campaign_start: '', campaign_end: '', enabled: true });
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [sponsorForm, setSponsorForm] = useState({ name: '', description: '', logo_url: '', logo_description: '', website: '', contact_email: '', categories: '', partner_type: 'Sponsor' });
  const [savingSponsor, setSavingSponsor] = useState(false);
  const [businessTypes, setBusinessTypes] = useState([]);

  const openPromoteToSponsor = (c) => {
    setPromotingCompany(c);
    setSponsorMode('create');
    setExistingSponsorId(null);
    setSponsorBanners([]);
    setSponsorForm({
      name: c.name || '',
      description: c.description || '',
      logo_url: c.logo_url || '',
      logo_description: c.tagline || '',
      website: c.website || '',
      contact_email: c.contact_email || '',
      categories: (c.sustainability_focus || []).join(', '),
      partner_type: 'Sponsor',
    });
    setShowSponsorDialog(true);
  };

  const openManageSponsor = async (c) => {
    if (!c.sponsor_id) { toast.error('No linked sponsor id'); return; }
    setPromotingCompany(c);
    setSponsorMode('manage');
    setExistingSponsorId(c.sponsor_id);
    try {
      const [sp, bn] = await Promise.all([
        fetch(`${BACKEND_URL}/api/sponsors/${c.sponsor_id}`, { credentials: 'include' }).then(r => r.json()),
        fetch(`${BACKEND_URL}/api/sponsors/${c.sponsor_id}/banners`, { credentials: 'include' }).then(r => r.json()),
      ]);
      setSponsorForm({
        name: sp.name || c.name || '',
        description: sp.description || '',
        logo_url: sp.logo_url || '',
        logo_description: sp.logo_description || '',
        website: sp.website || '',
        contact_email: sp.contact_email || '',
        categories: (sp.categories || []).join(', '),
        partner_type: sp.partner_type || 'Sponsor',
      });
      setSponsorBanners(Array.isArray(bn) ? bn : (bn?.banners || []));
    } catch { toast.error('Failed to load sponsor'); }
    setBannerForm({ banner_type: 'top', placement: 'app', click_url: '', file: null, campaign_start: '', campaign_end: '', enabled: true });
    setShowSponsorDialog(true);
  };

  const savePromoteToSponsor = async () => {
    if (!sponsorForm.name.trim()) { toast.error('Sponsor name is required'); return; }
    if (!sponsorForm.description.trim()) { toast.error('Description is required'); return; }
    setSavingSponsor(true);
    try {
      const payload = {
        ...sponsorForm,
        categories: sponsorForm.categories.split(',').map(c => c.trim()).filter(Boolean),
      };
      let url;
      let method;
      if (sponsorMode === 'manage' && existingSponsorId) {
        url = `${BACKEND_URL}/api/sponsors/${existingSponsorId}`;
        method = 'PUT';
      } else {
        url = `${BACKEND_URL}/api/sponsors`;
        method = 'POST';
        payload.linked_company_id = promotingCompany?.company_id;
      }
      const res = await fetch(url, {
        method, credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Failed to save sponsor');
      }
      const saved = await res.json();
      if (sponsorMode === 'create') {
        // After creating, switch to manage mode so admin can add banners straight away
        setSponsorMode('manage');
        setExistingSponsorId(saved.sponsor_id);
        toast.success(`${sponsorForm.name} is now a sponsor. Add banners below.`);
      } else {
        toast.success('Sponsor updated');
      }
      load();
    } catch (e) { toast.error(e.message || 'Failed'); }
    setSavingSponsor(false);
  };

  const uploadBanner = async () => {
    if (!existingSponsorId) { toast.error('Save sponsor first'); return; }
    if (!bannerForm.file) { toast.error('Please pick a banner image'); return; }
    setUploadingBanner(true);
    try {
      const fd = new FormData();
      fd.append('file', bannerForm.file);
      fd.append('banner_type', bannerForm.banner_type);
      fd.append('placement', bannerForm.placement);
      fd.append('click_url', bannerForm.click_url);
      fd.append('enabled', String(bannerForm.enabled));
      if (bannerForm.campaign_start) fd.append('campaign_start', bannerForm.campaign_start);
      if (bannerForm.campaign_end) fd.append('campaign_end', bannerForm.campaign_end);
      const res = await fetch(`${BACKEND_URL}/api/sponsors/${existingSponsorId}/banners`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Banner upload failed');
      }
      const created = await res.json();
      setSponsorBanners(prev => [...prev, created]);
      setBannerForm({ banner_type: 'top', placement: 'app', click_url: '', file: null, campaign_start: '', campaign_end: '', enabled: true });
      toast.success('Banner added');
    } catch (e) { toast.error(e.message || 'Failed'); }
    setUploadingBanner(false);
  };

  const toggleBanner = async (banner) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/sponsors/banners/${banner.banner_id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !banner.enabled }),
      });
      if (!res.ok) throw new Error('Failed');
      setSponsorBanners(prev => prev.map(b => b.banner_id === banner.banner_id ? { ...b, enabled: !banner.enabled } : b));
    } catch { toast.error('Failed to toggle banner'); }
  };

  const deleteBanner = async (banner) => {
    if (!(await window.appConfirm('Delete this banner?'))) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/sponsors/banners/${banner.banner_id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed');
      setSponsorBanners(prev => prev.filter(b => b.banner_id !== banner.banner_id));
      toast.success('Banner removed');
    } catch { toast.error('Failed to delete'); }
  };

  const removeSponsorshipFromCompany = async () => {
    if (!existingSponsorId) return;
    if (!(await window.appConfirm('Remove sponsor status from this company? This deletes the sponsor record and all of its banners.'))) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/sponsors/${existingSponsorId}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed');
      toast.success('Sponsor removed');
      setShowSponsorDialog(false);
      load();
    } catch { toast.error('Failed to remove'); }
  };

  useEffect(() => { load(); }, []);

  // Load admin-managed list of business types — used for the dropdown.
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/business-types`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.business_types) setBusinessTypes(d.business_types); })
      .catch(() => {});
  }, []);

  const load = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/companies?page=1&limit=100&status=all`, { credentials: 'include' });
      if (res.ok) { const d = await res.json(); setCompanies(d.companies || []); }
    } catch {}
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    try {
      const url = editing ? `${BACKEND_URL}/api/companies/${editing.company_id}` : `${BACKEND_URL}/api/companies`;
      const method = editing ? 'PUT' : 'POST';
      const res = await fetch(url, { method, credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
      toast.success(editing ? 'Company updated' : 'Company created');
      setShowForm(false); setEditing(null); setForm({ ...emptyForm }); load();
    } catch (e) { toast.error(e.message); }
  };

  const changeStatus = async (id, status) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/companies/${id}/status`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
      toast.success(`Company ${status}`); load();
    } catch (e) { toast.error(e.message || 'Failed to update status'); }
  };

  const del = async (id) => {
    if (!(await window.appConfirm('Delete this company permanently?'))) return;
    try {
      await fetch(`${BACKEND_URL}/api/companies/${id}`, { method: 'DELETE', credentials: 'include' });
      toast.success('Deleted'); load();
    } catch { toast.error('Failed'); }
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || '', description: c.description || '', tagline: c.tagline || '',
      sector: c.sector || '', business_type: c.business_type || '', company_size: c.company_size || '',
      hq_location: c.hq_location || '', hq_country: c.hq_country || '',
      website: c.website || '', linkedin_url: c.linkedin_url || '',
      contact_email: c.contact_email || '', phone_number: c.phone_number || '',
      is_partner: c.is_partner || false, sustainability_focus: c.sustainability_focus || [],
      about_sections: c.about_sections || [], latest_news: c.latest_news || [],
    });
    setShowForm(true);
  };

  const uploadCompanyAsset = async (assetType, file) => {
    if (!editing?.company_id) {
      toast.error('Save the company first, then upload assets');
      return null;
    }
    const fd = new FormData();
    fd.append('asset_type', assetType);
    fd.append('file', file);
    const res = await fetch(`${BACKEND_URL}/api/companies/${editing.company_id}/upload-asset`, {
      method: 'POST', credentials: 'include', body: fd,
    });
    if (!res.ok) { toast.error('Upload failed'); return null; }
    return await res.json();
  };

  const filtered = statusFilter === 'all' ? companies : companies.filter(c => c.status === statusFilter);
  const counts = { all: companies.length, approved: companies.filter(c => c.status === 'approved').length, pending: companies.filter(c => c.status === 'pending').length, rejected: companies.filter(c => c.status === 'rejected').length };

  return (
    <div className="space-y-4" data-testid="admin-companies-content">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xl font-bold font-heading">Company Management</h2>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={load} data-testid="refresh-companies-btn">
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Refresh
          </Button>
          <Button size="sm" onClick={() => { setEditing(null); setForm({ ...emptyForm }); setShowForm(true); }}
            className="bg-gradient-to-r from-brand-navy to-brand-sage text-white" data-testid="admin-create-company-btn">
            <PlusCircle className="h-3.5 w-3.5 mr-1.5" /> Create Company
          </Button>
        </div>
      </div>

      {/* Status filter tabs */}
      <div className="flex gap-2 flex-wrap" data-testid="company-status-filters">
        {['all', 'approved', 'pending', 'rejected'].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)} data-testid={`filter-${s}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              statusFilter === s ? 'bg-brand-navy text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}>
            {s.charAt(0).toUpperCase() + s.slice(1)} ({counts[s]})
          </button>
        ))}
      </div>

      {/* Companies List */}
      {filtered.length === 0 ? (
        <Card className="p-8 text-center">
          <Building2 className="h-8 w-8 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-500 text-sm">{statusFilter === 'all' ? 'No companies yet.' : `No ${statusFilter} companies.`}</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(c => (
            <Card key={c.company_id}
              className={`p-4 ${c.status === 'pending' ? 'border-l-4 border-l-amber-400' : c.status === 'rejected' ? 'border-l-4 border-l-red-300' : ''}`}
              data-testid={`admin-company-${c.company_id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-bold text-slate-800">{c.name}</h4>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                      c.status === 'approved' ? 'bg-green-100 text-green-700' :
                      c.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    }`}>{c.status?.toUpperCase()}</span>
                    {c.is_partner && <span className="text-[10px] px-2 py-0.5 rounded-full bg-brand-navy/10 text-brand-navy font-semibold">PARTNER</span>}
                    {c.is_sponsor && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold inline-flex items-center gap-0.5"><Award className="h-2.5 w-2.5" />SPONSOR</span>}
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                    {c.sector && <span><Building2 className="h-3 w-3 inline mr-1" />{c.sector}</span>}
                    {c.business_type && <span className="text-brand-navy font-medium" data-testid={`company-business-type-${c.company_id}`}>{c.business_type}</span>}
                    {c.hq_location && <span><MapPin className="h-3 w-3 inline mr-1" />{c.hq_location}</span>}
                    {c.company_size && <span><UsersIcon className="h-3 w-3 inline mr-1" />{c.company_size}</span>}
                    {c.website && <span><Globe className="h-3 w-3 inline mr-1" />{c.website.replace(/^https?:\/\//, '').split('/')[0]}</span>}
                  </div>
                </div>
                <div className="flex gap-1.5 flex-shrink-0 items-center">
                  {c.status === 'approved' && (
                    c.is_sponsor ? (
                      <Button size="sm" variant="outline" onClick={() => openManageSponsor(c)}
                        className="text-amber-700 border-amber-300 bg-amber-50 hover:bg-amber-100"
                        data-testid={`manage-sponsor-${c.company_id}`}>
                        <Award className="h-3.5 w-3.5 mr-1" /> Manage Sponsor
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => openPromoteToSponsor(c)}
                        className="text-amber-600 border-amber-200 hover:bg-amber-50"
                        data-testid={`make-sponsor-${c.company_id}`}>
                        <Sparkles className="h-3.5 w-3.5 mr-1" /> Make Sponsor
                      </Button>
                    )
                  )}
                  <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-company-${c.company_id}`}>
                    <Edit className="h-3.5 w-3.5" />
                  </Button>
                  {c.status !== 'approved' && (
                    <Button size="sm" onClick={() => changeStatus(c.company_id, 'approved')}
                      className="bg-green-600 hover:bg-green-700 text-white" data-testid={`approve-company-${c.company_id}`}>
                      <Check className="h-3.5 w-3.5 mr-1" /> Approve
                    </Button>
                  )}
                  {c.status === 'approved' && (
                    <Button size="sm" variant="outline" onClick={() => changeStatus(c.company_id, 'pending')}
                      className="text-amber-600 border-amber-200 hover:bg-amber-50" data-testid={`unapprove-company-${c.company_id}`}>
                      <Clock className="h-3.5 w-3.5 mr-1" /> Unapprove
                    </Button>
                  )}
                  {c.status === 'rejected' && (
                    <Button size="sm" onClick={() => changeStatus(c.company_id, 'pending')}
                      className="bg-amber-500 hover:bg-amber-600 text-white" data-testid={`restore-company-${c.company_id}`}>
                      <RotateCcw className="h-3.5 w-3.5 mr-1" /> Restore
                    </Button>
                  )}
                  {c.status !== 'rejected' && (
                    <Button size="sm" variant="outline" onClick={() => changeStatus(c.company_id, 'rejected')}
                      className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`reject-company-${c.company_id}`}>
                      <XIcon className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-600" onClick={() => del(c.company_id)} data-testid={`delete-company-${c.company_id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Company Form Dialog */}
      <Dialog open={showForm} onOpenChange={(v) => { setShowForm(v); if (!v) setEditing(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="company-form-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Edit Company' : 'Create Company'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Company Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="company-name-input" /></div>
              <div><Label>Tagline</Label><Input value={form.tagline} onChange={e => setForm({ ...form, tagline: e.target.value })} /></div>
            </div>
            <div><Label>Description</Label><RichTextEditor value={form.description} onChange={v => setForm({ ...form, description: v })} testId="company-description-input" minHeight="8rem" placeholder="Company description shown on their public profile" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Sector</Label>
                <select className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" value={form.sector} onChange={e => setForm({ ...form, sector: e.target.value })}>
                  <option value="">Select sector</option>
                  {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <Label>Business Type</Label>
                <select className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" value={form.business_type}
                  onChange={e => setForm({ ...form, business_type: e.target.value })}
                  data-testid="company-business-type-select">
                  <option value="">Select business type</option>
                  {businessTypes.map(bt => <option key={bt} value={bt}>{bt}</option>)}
                </select>
                <p className="text-[10px] text-slate-400 mt-0.5">Manage this list under Admin → Business Types.</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Company Size</Label>
                <select className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" value={form.company_size} onChange={e => setForm({ ...form, company_size: e.target.value })}>
                  <option value="">Select size</option>
                  {SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div><Label>HQ Location</Label><Input value={form.hq_location} onChange={e => setForm({ ...form, hq_location: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>HQ Country</Label><Input value={form.hq_country} onChange={e => setForm({ ...form, hq_country: e.target.value })} /></div>
              <div></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Website</Label><Input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} /></div>
              <div><Label>LinkedIn URL</Label><Input value={form.linkedin_url} onChange={e => setForm({ ...form, linkedin_url: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Contact Email</Label><Input type="email" value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })} /></div>
              <div><Label>Phone Number</Label><Input value={form.phone_number} onChange={e => setForm({ ...form, phone_number: e.target.value })} /></div>
            </div>
            <div>
              <Label>Sustainability Focus</Label>
              <SustainabilityTagPicker value={form.sustainability_focus || []} onChange={v => setForm(p => ({ ...p, sustainability_focus: v }))} onTagsChanged={load} />
              {(form.sustainability_focus || []).length > 0 && <p className="text-xs text-slate-400 mt-1">{form.sustainability_focus.length} selected</p>}
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="is-partner" checked={form.is_partner} onChange={e => setForm({ ...form, is_partner: e.target.checked })} data-testid="is-partner-checkbox" />
              <Label htmlFor="is-partner" className="cursor-pointer">Mark as Partner</Label>
            </div>

            {/* ───── About Us — Collateral & Media (up to 5 cards) ───── */}
            <div className="border-t pt-4" data-testid="company-about-sections">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-base">Collateral & Media — up to 5 cards</Label>
                {(form.about_sections || []).length < 5 && (
                  <Button type="button" size="sm" variant="outline"
                    onClick={() => setForm(p => ({ ...p, about_sections: [...(p.about_sections || []), { title: '', description: '', media_url: '', media_type: 'pdf' }] }))}
                    data-testid="add-about-section-btn">
                    <PlusCircle className="h-3 w-3 mr-1" /> Add card
                  </Button>
                )}
              </div>
              {(form.about_sections || []).length === 0 && (
                <p className="text-xs text-slate-400 italic">No cards yet. Click "Add card" to attach a PDF brochure, case study or Vimeo video.</p>
              )}
              {(form.about_sections || []).map((sec, idx) => (
                <div key={idx} className="p-3 border border-slate-200 rounded mb-2 space-y-2 bg-slate-50" data-testid={`about-section-${idx}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-slate-500">Card {idx + 1}</p>
                    <button type="button" className="text-red-500 text-xs"
                      onClick={() => setForm(p => ({ ...p, about_sections: p.about_sections.filter((_, i) => i !== idx) }))}>
                      <XIcon className="h-3 w-3" />
                    </button>
                  </div>
                  <Input placeholder="Title (e.g. Capability Statement)" value={sec.title}
                    onChange={e => { const arr = [...form.about_sections]; arr[idx].title = e.target.value; setForm({ ...form, about_sections: arr }); }} />
                  <RichTextEditor placeholder="Short description" value={sec.description}
                    onChange={v => { const arr = [...form.about_sections]; arr[idx].description = v; setForm({ ...form, about_sections: arr }); }}
                    testId={`company-about-section-desc-${idx}`} minHeight="5rem" />
                  <div className="flex gap-2 items-center">
                    <select className="rounded-md border border-slate-200 px-2 py-1.5 text-sm bg-white w-28"
                      value={sec.media_type || 'pdf'}
                      onChange={e => { const arr = [...form.about_sections]; arr[idx].media_type = e.target.value; setForm({ ...form, about_sections: arr }); }}>
                      <option value="pdf">PDF</option>
                      <option value="vimeo">Vimeo</option>
                    </select>
                    {sec.media_type === 'vimeo' ? (
                      <Input placeholder="https://vimeo.com/..." value={sec.media_url}
                        onChange={e => { const arr = [...form.about_sections]; arr[idx].media_url = e.target.value; setForm({ ...form, about_sections: arr }); }} />
                    ) : (
                      <>
                        <Input placeholder="PDF URL or upload →" value={sec.media_url}
                          onChange={e => { const arr = [...form.about_sections]; arr[idx].media_url = e.target.value; setForm({ ...form, about_sections: arr }); }} />
                        {editing && (
                          <input type="file" accept="application/pdf" className="text-xs"
                            data-testid={`about-section-file-${idx}`}
                            onChange={async e => {
                              const f = e.target.files?.[0]; if (!f) return;
                              const r = await uploadCompanyAsset('about_pdf', f);
                              if (r) {
                                const arr = [...form.about_sections];
                                arr[idx].media_url = r.url;
                                arr[idx].media_filename = r.filename;
                                setForm({ ...form, about_sections: arr });
                                toast.success('PDF uploaded');
                              }
                            }} />
                        )}
                      </>
                    )}
                  </div>
                  {sec.media_filename && <p className="text-[10px] text-slate-400">{sec.media_filename}</p>}
                </div>
              ))}
              {!editing && (
                <p className="text-[11px] text-amber-600 mt-1 italic">Tip: Save the company first to enable PDF/image uploads.</p>
              )}
            </div>

            {/* ───── Latest News (up to 3 articles) ───── */}
            <div className="border-t pt-4" data-testid="company-latest-news">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-base">Latest News — up to 3 articles</Label>
                {(form.latest_news || []).length < 3 && (
                  <Button type="button" size="sm" variant="outline"
                    onClick={() => setForm(p => ({ ...p, latest_news: [...(p.latest_news || []), { title: '', overview: '', image_url: '', article_url: '' }] }))}
                    data-testid="add-news-article-btn">
                    <PlusCircle className="h-3 w-3 mr-1" /> Add article
                  </Button>
                )}
              </div>
              {(form.latest_news || []).length === 0 && (
                <p className="text-xs text-slate-400 italic">No news articles yet.</p>
              )}
              {(form.latest_news || []).map((art, idx) => (
                <div key={idx} className="p-3 border border-slate-200 rounded mb-2 space-y-2 bg-slate-50" data-testid={`news-article-${idx}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-slate-500">Article {idx + 1}</p>
                    <button type="button" className="text-red-500 text-xs"
                      onClick={() => setForm(p => ({ ...p, latest_news: p.latest_news.filter((_, i) => i !== idx) }))}>
                      <XIcon className="h-3 w-3" />
                    </button>
                  </div>
                  <Input placeholder="Headline" value={art.title}
                    onChange={e => { const arr = [...form.latest_news]; arr[idx].title = e.target.value; setForm({ ...form, latest_news: arr }); }} />
                  <RichTextEditor placeholder="Overview / summary" value={art.overview}
                    onChange={v => { const arr = [...form.latest_news]; arr[idx].overview = v; setForm({ ...form, latest_news: arr }); }}
                    testId={`company-latest-news-overview-${idx}`} minHeight="5rem" />
                  <Input placeholder="Article URL (https://… or /news/:id)" value={art.article_url}
                    onChange={e => { const arr = [...form.latest_news]; arr[idx].article_url = e.target.value; setForm({ ...form, latest_news: arr }); }} />
                  <div className="flex items-center gap-2">
                    <Input placeholder="Image URL" value={art.image_url}
                      onChange={e => { const arr = [...form.latest_news]; arr[idx].image_url = e.target.value; setForm({ ...form, latest_news: arr }); }} />
                    {editing && (
                      <input type="file" accept="image/*" className="text-xs"
                        data-testid={`news-image-file-${idx}`}
                        onChange={async e => {
                          const f = e.target.files?.[0]; if (!f) return;
                          const r = await uploadCompanyAsset('news_image', f);
                          if (r) {
                            const arr = [...form.latest_news];
                            arr[idx].image_url = r.url;
                            setForm({ ...form, latest_news: arr });
                            toast.success('Image uploaded');
                          }
                        }} />
                    )}
                  </div>
                  {art.image_url && <img src={art.image_url} alt="" className="h-16 rounded object-cover" onError={(e) => e.target.style.display = 'none'} />}
                </div>
              ))}
            </div>

            <Button onClick={save} className="w-full bg-gradient-to-r from-brand-navy to-brand-sage text-white" data-testid="save-company-btn">
              {editing ? 'Update Company' : 'Create Company'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Promote-to-Sponsor / Manage-Sponsor Dialog */}
      <Dialog open={showSponsorDialog} onOpenChange={(v) => { setShowSponsorDialog(v); if (!v) { setPromotingCompany(null); setExistingSponsorId(null); setSponsorBanners([]); } }}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="promote-sponsor-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="h-5 w-5 text-amber-500" />
              {sponsorMode === 'manage' ? 'Manage Sponsor — ' : 'Make Sponsor — '}{promotingCompany?.name}
            </DialogTitle>
            <p className="text-xs text-slate-500 mt-1">
              {sponsorMode === 'manage'
                ? 'Edit sponsor details and manage their banner campaigns directly from this company.'
                : 'Promote this company to a sponsor. Save, then add banner campaigns below.'}
            </p>
          </DialogHeader>

          {/* SPONSOR DETAILS SECTION */}
          <div className="space-y-3 border-b border-slate-100 pb-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Sponsor Name *</Label><Input value={sponsorForm.name} onChange={e => setSponsorForm({ ...sponsorForm, name: e.target.value })} data-testid="sponsor-name-input" /></div>
              <div>
                <Label>Sponsor Tier</Label>
                <select className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm bg-white" value={sponsorForm.partner_type} onChange={e => setSponsorForm({ ...sponsorForm, partner_type: e.target.value })} data-testid="sponsor-tier-select">
                  <option value="Sponsor">Sponsor</option>
                  <option value="Premium Sponsor">Premium Sponsor</option>
                  <option value="Partner">Partner</option>
                  <option value="Exhibitor">Exhibitor</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Description *</Label>
              <RichTextEditor value={sponsorForm.description} onChange={v => setSponsorForm({ ...sponsorForm, description: v })} testId="sponsor-description-input-2" minHeight="7rem" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Logo URL</Label><Input value={sponsorForm.logo_url} onChange={e => setSponsorForm({ ...sponsorForm, logo_url: e.target.value })} placeholder="https://…" /></div>
              <div><Label>Website</Label><Input value={sponsorForm.website} onChange={e => setSponsorForm({ ...sponsorForm, website: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Contact Email</Label><Input type="email" value={sponsorForm.contact_email} onChange={e => setSponsorForm({ ...sponsorForm, contact_email: e.target.value })} /></div>
              <div><Label>Categories (comma-separated)</Label><Input value={sponsorForm.categories} onChange={e => setSponsorForm({ ...sponsorForm, categories: e.target.value })} placeholder="Net Zero, Logistics" /></div>
            </div>
            {sponsorForm.logo_url && (
              <div className="flex items-center gap-2 p-2 border border-slate-200 rounded bg-slate-50">
                <img src={sponsorForm.logo_url} alt="logo preview" className="h-10 w-10 object-contain bg-white rounded" onError={(e) => e.target.style.display = 'none'} />
                <p className="text-xs text-slate-500">Logo preview</p>
              </div>
            )}
            <div className="flex justify-between items-center pt-2">
              {sponsorMode === 'manage' ? (
                <Button variant="outline" onClick={removeSponsorshipFromCompany}
                  className="text-red-600 border-red-200 hover:bg-red-50" data-testid="remove-sponsor-btn">
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Remove sponsorship
                </Button>
              ) : <span />}
              <Button onClick={savePromoteToSponsor} disabled={savingSponsor}
                className="bg-gradient-to-r from-amber-500 to-amber-600 text-white"
                data-testid="confirm-make-sponsor-btn">
                {savingSponsor ? 'Saving…' : (sponsorMode === 'manage' ? 'Save Sponsor' : 'Create Sponsor')}
              </Button>
            </div>
          </div>

          {/* BANNER CAMPAIGN SECTION — only after sponsor is created */}
          {existingSponsorId && (
            <div className="pt-2 space-y-3">
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-bold text-brand-navy">Banner Campaigns</h4>
                <span className="text-xs text-slate-400">({sponsorBanners.length})</span>
              </div>

              {/* Existing banners list */}
              {sponsorBanners.length > 0 ? (
                <div className="space-y-2" data-testid="sponsor-banner-list">
                  {sponsorBanners.map(b => (
                    <div key={b.banner_id} className="flex items-center gap-3 p-2 border border-slate-200 rounded-lg bg-white" data-testid={`banner-row-${b.banner_id}`}>
                      <img src={b.image_url} alt="" className="h-12 w-24 object-contain bg-slate-50 rounded border border-slate-100" onError={(e) => e.target.style.display = 'none'} />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-slate-700 capitalize">{b.banner_type} · {b.placement}</p>
                        {b.click_url && <p className="text-[11px] text-slate-400 truncate">{b.click_url}</p>}
                        {(b.campaign_start || b.campaign_end) && (
                          <p className="text-[10px] text-slate-400">{b.campaign_start || '—'} → {b.campaign_end || '—'}</p>
                        )}
                      </div>
                      <button type="button" onClick={() => toggleBanner(b)}
                        className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${b.enabled ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}
                        data-testid={`toggle-banner-${b.banner_id}`}>
                        {b.enabled ? 'Enabled' : 'Disabled'}
                      </button>
                      <Button size="sm" variant="ghost" onClick={() => deleteBanner(b)} data-testid={`delete-banner-${b.banner_id}`}>
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-400 italic">No banner campaigns yet. Add one below.</p>
              )}

              {/* Add banner form */}
              <Card className="p-3 bg-amber-50/50 border-amber-100">
                <p className="text-xs font-bold text-amber-700 mb-2">Add a banner</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Type</Label>
                    <select className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm bg-white"
                      value={bannerForm.banner_type} onChange={e => setBannerForm({ ...bannerForm, banner_type: e.target.value })} data-testid="banner-type-select">
                      <option value="top">Top banner</option>
                      <option value="side">Side banner</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">Placement</Label>
                    <select className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm bg-white"
                      value={bannerForm.placement} onChange={e => setBannerForm({ ...bannerForm, placement: e.target.value })} data-testid="banner-placement-select">
                      <option value="app">App-wide</option>
                      <option value="dashboard">Dashboard</option>
                      <option value="forum">Forum</option>
                      <option value="events">Events</option>
                      <option value="courses">Courses</option>
                      <option value="britelabs">BriteLabs</option>
                      <option value="news">News</option>
                    </select>
                  </div>
                </div>
                <div className="mt-2">
                  <Label className="text-xs">Banner image *</Label>
                  <Input type="file" accept="image/*"
                    onChange={e => setBannerForm({ ...bannerForm, file: e.target.files?.[0] || null })}
                    data-testid="banner-file-input" />
                  {bannerForm.file && <p className="text-[10px] text-slate-400 mt-1">{bannerForm.file.name} · {(bannerForm.file.size / 1024).toFixed(0)} KB</p>}
                </div>
                <div className="mt-2">
                  <Label className="text-xs">Click-through URL</Label>
                  <Input value={bannerForm.click_url} onChange={e => setBannerForm({ ...bannerForm, click_url: e.target.value })}
                    placeholder="https://sponsor.com/landing" data-testid="banner-click-url" />
                </div>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <div>
                    <Label className="text-xs">Campaign start</Label>
                    <Input type="date" value={bannerForm.campaign_start} onChange={e => setBannerForm({ ...bannerForm, campaign_start: e.target.value })} data-testid="banner-start-date" />
                  </div>
                  <div>
                    <Label className="text-xs">Campaign end</Label>
                    <Input type="date" value={bannerForm.campaign_end} onChange={e => setBannerForm({ ...bannerForm, campaign_end: e.target.value })} data-testid="banner-end-date" />
                  </div>
                </div>
                <div className="flex items-center justify-end gap-2 mt-3">
                  <Button onClick={uploadBanner} disabled={uploadingBanner || !bannerForm.file}
                    className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="add-banner-btn">
                    {uploadingBanner ? 'Uploading…' : 'Add banner'}
                  </Button>
                </div>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
